<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-500 to-indigo-100">
    <!-- Header -->
    <header class="bg-[#0a4068] shadow-2xl border-b-4 border-blue-300">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
          <div class="flex items-center space-x-3 sm:space-x-5 w-full sm:w-auto">
            <div class="relative">
              <div class="absolute inset-0 bg-white rounded-full blur-sm opacity-30"></div>
              <img :src="logoUrl" alt="AUAS Logo" class="relative h-10 w-10 sm:h-16 sm:w-16 flex-shrink-0 rounded-full border-3 border-white shadow-lg transform hover:scale-105 transition-transform duration-300" />
            </div>
            <div class="min-w-0 flex-1">
              <h1 class="text-lg sm:text-2xl lg:text-4xl font-bold text-white leading-tight tracking-wide drop-shadow-2xl">
                <span class="text-white font-extrabold">Academic Staff</span>
                <br class="sm:hidden" />
                <span class="text-white"> Information Form</span>
              </h1>
              <div class="mt-2">
                <SaveStatus :saving="form.saving" :lastSaved="form.lastSaved" />
              </div>
            </div>
          </div>
          <div class="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <router-link
              to="/dashboard"
              class="inline-flex items-center justify-center px-4 sm:px-5 py-2.5 sm:py-3 border-2 border-white/30 rounded-lg shadow-lg text-xs sm:text-sm font-semibold text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent whitespace-nowrap transition-all duration-300 transform hover:scale-105"
            >
              <svg class="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              <span class="hidden sm:inline">Back to Dashboard</span>
              <span class="sm:hidden">Dashboard</span>
            </router-link>
            <button
              v-if="true"
              type="button"
              @click="submitForm"
              :disabled="!canSubmit"
              class="inline-flex items-center justify-center px-5 sm:px-7 py-2.5 sm:py-3 border border-transparent text-sm sm:text-base font-bold rounded-lg shadow-xl text-white bg-gradient-to-r from-emerald-500 via-green-600 to-teal-600 hover:from-emerald-600 hover:via-green-700 hover:to-teal-700 focus:outline-none focus:ring-4 focus:ring-green-300/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap"
            >
              <svg v-if="submitting" class="animate-spin -ml-1 mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="flex items-center">
                <svg v-if="!submitting" class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                </svg>
                {{ submitting ? 'Saving...' : (form.status === 'submitted' ? 'Update Information' : 'Submit Form') }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <form @submit.prevent="submitForm" novalidate class="space-y-8">
        
        <!-- Progress Indicator -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">Form Progress</h2>
            <span class="text-sm text-gray-500">{{ completedSections }}/{{ totalSections }} sections completed</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div 
              class="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300" 
              :style="{ width: `${(completedSections / totalSections) * 100}%` }"
            ></div>
          </div>
          <p class="mt-4 text-sm text-gray-600">
            Submission requires Personal Information, Institution Information, a formal photo, and at least one Education entry. The remaining sections are optional.
          </p>
        </div>

        <ErrorDisplay
          v-if="showValidationErrors && (form.errors.length > 0 || form.submitError)"
          :errors="form.errors"
          :global-error="form.submitError"
        />

        <!-- Personal Information Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              Personal Information <span class="text-red-500">&nbsp;*</span>
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">Personal Information</legend>
              
              <!-- Name Fields -->
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div class="form-group">
                  <label for="name_en" class="block text-sm font-medium text-gray-700 mb-2">
                    Full Name (English) <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="name_en"
                    v-model="formData.personal_info.name_en"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your full name in English"
                  />
                </div>
                <div class="form-group">
                  <label for="name_ar" class="block text-sm font-medium text-gray-700 mb-2">
                    الاسم الكامل (عربي) <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="name_ar"
                    v-model="formData.personal_info.name_ar"
                    type="text"
                    required
                    dir="rtl"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your full name in Arabic"
                  />
                </div>
                <div class="form-group">
                  <label for="name_ku" class="block text-sm font-medium text-gray-700 mb-2">
                    ناوی تەواو (کوردی) <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="name_ku"
                    v-model="formData.personal_info.name_ku"
                    type="text"
                    required
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your full name in Kurdish"
                  />
                </div>
              </div>

              <!-- Title Fields -->
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div class="form-group">
                  <label for="title_en" class="block text-sm font-medium text-gray-700 mb-2">
                    Academic Title (English) <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="title_en"
                    v-model="formData.personal_info.title_en"
                    type="text"
                    required
                    :class="[
                      'form-control',
                      hasFieldError('title_en') ? 'border-red-500 focus:ring-red-500' : ''
                    ]"
                    placeholder="e.g., Professor, Associate Professor"
                    @input="clearFieldError('title_en')"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('title_en')"
                    :errors="form.errors"
                    field-name="title_en"
                  />
                </div>

                <div class="form-group">
                  <label for="title_ar" class="block text-sm font-medium text-gray-700 mb-2">
                    اللقب الأكاديمي (عربي) <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="title_ar"
                    v-model="formData.personal_info.title_ar"
                    type="text"
                    required
                    class="form-control"
                    placeholder="Enter academic title in Arabic"
                    dir="rtl"
                  />
                </div>

                <div class="form-group">
                  <label for="title_ku" class="block text-sm font-medium text-gray-700 mb-2">
                    ناونیشانی زانستی (کوردی) <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="title_ku"
                    v-model="formData.personal_info.title_ku"
                    type="text"
                    required
                    class="form-control"
                    placeholder="Enter academic title in Kurdish"
                  />
                </div>
              </div>

              <!-- Personal Details -->
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div class="form-group">
                  <label for="gender" class="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span class="text-red-500">*</span>
                  </label>
                  <select
                    id="gender"
                    v-model="formData.personal_info.gender"
                    required
                    :class="[
                      'form-control',
                      hasFieldError('gender') ? 'border-red-500 focus:ring-red-500' : ''
                    ]"
                    @change="clearFieldError('gender')"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <ErrorDisplay 
                    v-if="hasFieldError('gender')"
                    :errors="form.errors"
                    field-name="gender"
                  />
                </div>

                <div class="form-group">
                  <label for="date_of_birth" class="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth <span class="text-red-500">*</span>
                  </label>
                  <DatePicker
                    v-model="formData.personal_info.date_of_birth"
                    :class="[
                      hasFieldError('date_of_birth') ? 'border-red-500 focus:ring-red-500' : ''
                    ]"
                    @update:modelValue="clearFieldError('date_of_birth')"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('date_of_birth')"
                    :errors="form.errors"
                    field-name="date_of_birth"
                  />
                </div>

                <div class="form-group">
                  <label for="nationality" class="block text-sm font-medium text-gray-700 mb-2">
                    Nationality <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="nationality"
                    v-model="formData.personal_info.nationality"
                    type="text"
                    required
                    :class="[
                      'form-control',
                      hasFieldError('nationality') ? 'border-red-500 focus:ring-red-500' : ''
                    ]"
                    placeholder="e.g., Iraqi, American"
                    @input="clearFieldError('nationality')"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('nationality')"
                    :errors="form.errors"
                    field-name="nationality"
                  />
                </div>
              </div>

              <!-- Contact Information -->
              <div class="grid grid-cols-1 gap-6">
                <div class="form-group">
                  <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    :value="auth.user?.email || formData.personal_info.email"
                    readonly
                    disabled
                    type="email"
                    class="form-control bg-gray-100 cursor-not-allowed opacity-75"
                    placeholder="your.email@example.com"
                  />
                  <p class="mt-1 text-xs text-gray-500">Email address is linked to your login and cannot be changed.</p>
                  <ErrorDisplay 
                    v-if="hasFieldError('email')"
                    :errors="form.errors"
                    field-name="email"
                  />
                </div>

                <div class="form-group">
                  <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    v-model="formData.personal_info.phone"
                    type="tel"
                    required
                    :class="[
                      'form-control',
                      hasFieldError('phone') ? 'border-red-500 focus:ring-red-500' : ''
                    ]"
                    placeholder="+964 750 123 4567"
                    @input="clearFieldError('phone')"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('phone')"
                    :errors="form.errors"
                    field-name="phone"
                  />
                </div>
              </div>
            </fieldset>
          </div>
        </section>

        <!-- Institution Information Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-purple-600 to-pink-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
              Institution Information <span class="text-red-500">&nbsp;*</span>
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">Institution Information</legend>
              
              <div class="grid grid-cols-1 gap-6">
                <div class="form-group">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    College/Institute & Department <span class="text-red-500">*</span>
                  </label>
                  <CollegeDepartmentSelect
                    v-model:college="formData.institution_info.college_institute"
                    v-model:department="formData.institution_info.department"
                    :class="[
                      hasFieldError('college_institute') || hasFieldError('department') ? 'border-red-500' : ''
                    ]"
                    @update:college="clearFieldError('college_institute')"
                    @update:department="clearFieldError('department')"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('college_institute')"
                    :errors="form.errors"
                    field-name="college_institute"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('department')"
                    :errors="form.errors"
                    field-name="department"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 gap-6">
                <div class="form-group">
                  <label for="specialization_en" class="block text-sm font-medium text-gray-700 mb-2">
                    Specialization (English) <span class="text-red-500">*</span>
                  </label>
                  <input
                    id="specialization_en"
                    v-model="formData.institution_info.specialization_en"
                    type="text"
                    :class="[
                      'form-control',
                      hasFieldError('specialization_en') ? 'border-red-500 focus:ring-red-500' : ''
                    ]"
                    placeholder="e.g., Computer Science, Mathematics"
                    @input="clearFieldError('specialization_en')"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('specialization_en')"
                    :errors="form.errors"
                    field-name="specialization_en"
                  />
                </div>

                <div class="form-group">
                  <label for="specialization_ar" class="block text-sm font-medium text-gray-700 mb-2">
                    Specialization (Arabic)
                  </label>
                  <input
                    id="specialization_ar"
                    v-model="formData.institution_info.specialization_ar"
                    type="text"
                    class="form-control"
                    placeholder="Enter specialization in Arabic"
                    dir="rtl"
                  />
                </div>

                <div class="form-group">
                  <label for="specialization_ku" class="block text-sm font-medium text-gray-700 mb-2">
                    Specialization (Kurdish)
                  </label>
                  <input
                    id="specialization_ku"
                    v-model="formData.institution_info.specialization_ku"
                    type="text"
                    class="form-control"
                    placeholder="Enter specialization in Kurdish"
                  />
                </div>
              </div>
            </fieldset>
          </div>
        </section>

        <!-- File Uploads Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-green-600 to-teal-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
              File Uploads
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">File Uploads</legend>
              
              <div class="grid grid-cols-1 xl:grid-cols-2 gap-8">
                <!-- Profile Photo Upload -->
                <div class="form-group">
                  <label class="block text-sm font-medium text-gray-700 mb-3">
                    <!-- Profile Photo <span class="text-red-500">*</span> -->
                    Formal Photo <span class="text-red-500">*</span>
                  </label>
                  <FileUploader
                    v-model="formData.files.photo"
                    accept="image/jpeg,image/png"
                    :max-size="15728640"
                    file-type="image"
                    required
                    @upload="handleFileUpload('photo', $event)"
                    :class="[
                      hasFieldError('photo') ? 'border-red-500' : ''
                    ]"
                  />
                  <ErrorDisplay 
                    v-if="hasFieldError('photo')"
                    :errors="form.errors"
                    field-name="photo"
                  />
                </div>
                
                <!-- CV Upload -->
                <div class="form-group">
                  <label class="block text-sm font-medium text-gray-700 mb-3">
                    Curriculum Vitae (PDF)
                  </label>
                  <FileUploader
                    v-model="formData.files.cv_file"
                    accept="application/pdf"
                    :max-size="10485760"
                    file-type="pdf"
                    @upload="handleFileUpload('cv_file', $event)"
                    :class="[
                      hasFieldError('cv_file') ? 'border-red-500' : ''
                    ]"
                  />
                  <p class="mt-2 text-xs text-gray-500">
                    Optional. Upload your latest CV as a PDF file.
                  </p>
                  <ErrorDisplay 
                    v-if="hasFieldError('cv_file')"
                    :errors="form.errors"
                    field-name="cv_file"
                  />
                </div>
              </div>
            </fieldset>
          </div>
        </section>

        <!-- Education Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-orange-600 to-red-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
              </svg>
              Education <span class="text-white-500"> &nbsp;*</span>
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">Education</legend>
              
              <!-- Certificate Note -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div class="flex items-start">
                  <svg class="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <p class="text-sm text-blue-800">
                    <strong>Note:</strong> Please include all education details including your degree, the college or university you attended, the years of study (start and end year), and the city and country where the institution is located..
                  </p>
                </div>
              </div>
              
              <div v-for="(education, index) in formData.education" :key="index" class="border border-gray-200 rounded-lg p-6 relative">
                <div class="flex justify-between items-start mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Education {{ index + 1 }}</h3>
                  <button
                    v-if="formData.education.length > 1"
                    type="button"
                    @click="removeEducationEntry(index)"
                    class="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>

                <ErrorDisplay
                  v-if="showValidationErrors"
                  :errors="form.errors"
                  :field-name="`education.${index}`"
                  class="mb-4"
                />

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="form-group">
                    <label :for="`degree_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Degree
                    </label>
                    <input
                      :id="`degree_${index}`"
                      v-model="education.degree"
                      type="text"
                      class="form-control"
                      placeholder="Degree"
                    />
                  </div>

                  <div class="form-group">
                    <label :for="`university_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      College,University
                    </label>
                    <input
                      :id="`university_${index}`"
                      v-model="education.university"
                      type="text"
                      class="form-control"
                      placeholder="College,University"
                    />
                  </div>

                  <div class="form-group">
                    <label :for="`year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Year Range
                    </label>
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label :for="`start_year_${index}`" class="block text-xs text-gray-500 mb-1">Start Year</label>
                        <select
                          :id="`start_year_${index}`"
                          v-model="education.start_year"
                          class="form-control text-sm"
                        >
                          <option value="">Select</option>
                          <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
                        </select>
                      </div>
                      <div>
                        <label :for="`end_year_${index}`" class="block text-xs text-gray-500 mb-1">End Year</label>
                        <select
                          :id="`end_year_${index}`"
                          v-model="education.end_year"
                          class="form-control text-sm"
                        >
                          <option value="">Select</option>
                          <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div class="form-group">
                    <label :for="`country_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      City, Country
                    </label>
                    <input
                      :id="`country_${index}`"
                      v-model="education.country"
                      type="text"
                      class="form-control"
                      placeholder="City, Country"
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                @click="addEducationEntry"
                class="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Another Education
              </button>
            </fieldset>
          </div>
        </section>

        <!-- Scientific Titles Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
              </svg>
              Scientific Titles
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">Scientific Titles</legend>
              
              <div v-for="(title, index) in formData.scientific_titles" :key="index" class="border border-gray-200 rounded-lg p-6 relative">
                <div class="flex justify-between items-start mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Scientific Title {{ index + 1 }}</h3>
                  <button
                    v-if="formData.scientific_titles.length > 1"
                    type="button"
                    @click="removeScientificTitleEntry(index)"
                    class="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div class="form-group">
                    <label :for="`title_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <select
                      :id="`title_${index}`"
                      v-model="title.title"
                      class="form-control"
                    >
                      <option value="">Select Title</option>
                      <option value="Assistant Lecturer">Assistant Lecturer</option>
                      <option value="Lecturer">Lecturer</option>
                      <option value="Assistant Professor">Assistant Professor</option>
                      <option value="Professor">Professor</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label :for="`document_number_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Document Number
                    </label>
                    <input
                      :id="`document_number_${index}`"
                      v-model="title.document_number"
                      type="text"
                      class="form-control"
                      placeholder="Document Number"
                    />
                  </div>

                  <div class="form-group">
                    <label :for="`title_year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    <input
                      :id="`title_year_${index}`"
                      v-model="title.year"
                      type="number"
                      min="1950"
                      :max="new Date().getFullYear() + 10"
                      class="form-control"
                      placeholder="Year"
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                @click="addScientificTitleEntry"
                class="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Another Scientific Title
              </button>
            </fieldset>
          </div>
        </section>

        <!-- Teaching Subjects Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-purple-600 to-pink-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
              Teaching Subjects
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">Teaching Subjects</legend>
              
              <div v-for="(subject, index) in formData.teaching_subjects" :key="index" class="border border-gray-200 rounded-lg p-6 relative">
                <div class="flex justify-between items-start mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Teaching Subject {{ index + 1 }}</h3>
                  <button
                    v-if="formData.teaching_subjects.length > 1"
                    type="button"
                    @click="removeTeachingSubjectEntry(index)"
                    class="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div class="form-group">
                    <label :for="`subject_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <input
                      :id="`subject_${index}`"
                      v-model="subject.subject"
                      type="text"
                      class="form-control"
                      placeholder="Subject Name"
                    />
                  </div>

                  <div class="form-group">
                    <label :for="`college_institute_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      College/Institute
                    </label>
                    <select
                      :id="`college_institute_${index}`"
                      v-model="subject.college_institute"
                      class="form-control"
                    >
                      <option value="">Select Type</option>
                      <option value="college">College</option>
                      <option value="institute">Institute</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label :for="`semester_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Semester
                    </label>
                    <select
                      :id="`semester_${index}`"
                      v-model="subject.semester"
                      class="form-control"
                      :disabled="!subject.college_institute"
                    >
                      <option value="">Select Semester</option>
                      <option v-if="subject.college_institute === 'institute'" v-for="n in 4" :key="n" :value="n">{{ n }}</option>
                      <option v-if="subject.college_institute === 'college'" v-for="n in 8" :key="n" :value="n">{{ n }}</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label :for="`teaching_year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    <select
                      :id="`teaching_year_${index}`"
                      v-model="subject.year"
                      class="form-control"
                    >
                      <option value="">Select Year</option>
                      <option v-for="year in getYearOptions()" :key="year" :value="year">{{ year }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <button
                type="button"
                @click="addTeachingSubjectEntry"
                class="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Another Teaching Subject
              </button>
            </fieldset>
          </div>
        </section>

        <!-- Research Publications Section -->
        <!-- <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
              Research Publications
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">Research Publications</legend>
              
              <div v-for="(publication, index) in formData.research_publications" :key="index" class="border border-gray-200 rounded-lg p-6 relative">
                <div class="flex justify-between items-start mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Publication {{ index + 1 }}</h3>
                  <button
                    v-if="formData.research_publications.length > 1"
                    type="button"
                    @click="removePublicationEntry(index)"
                    class="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>

                <div class="grid grid-cols-1 gap-6">
                  <div class="form-group">
                    <label :for="`title_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Title <span class="text-red-500">*</span>
                    </label>
                    <input
                      :id="`title_${index}`"
                      v-model="publication.title"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`research_publications.${index}.title`) ? 'border-red-500 focus:ring-red-500' : ''
                      ]"
                      placeholder="Enter the publication title"
                      @input="clearFieldError(`research_publications.${index}.title`)"
                    />
                    <ErrorDisplay 
                      v-if="hasFieldError(`research_publications.${index}.title`)"
                      :errors="form.errors"
                      :field-name="`research_publications.${index}.title`"
                    />
                  </div>

                  <div class="grid grid-cols-1 gap-6">
                    <div class="form-group">
                      <label :for="`cited_by_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                        Cited By
                      </label>
                      <input
                        :id="`cited_by_${index}`"
                        v-model="publication.cited_by"
                        type="number"
                        min="0"
                        class="form-control"
                        placeholder="e.g., 25"
                      />
                    </div>

                    <div class="form-group">
                      <label :for="`year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                        Year <span class="text-red-500">*</span>
                      </label>
                      <input
                        :id="`year_${index}`"
                        v-model="publication.year"
                        type="number"
                        min="1950"
                        :max="new Date().getFullYear() + 1"
                        :class="[
                          'form-control',
                          hasFieldError(`research_publications.${index}.year`) ? 'border-red-500 focus:ring-red-500' : ''
                        ]"
                        placeholder="e.g., 2023"
                        @input="clearFieldError(`research_publications.${index}.year`)"
                      />
                      <ErrorDisplay 
                        v-if="hasFieldError(`research_publications.${index}.year`)"
                        :errors="form.errors"
                        :field-name="`research_publications.${index}.year`"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Google Scholar Integration - ->
              <div class="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 class="text-lg font-medium text-blue-900 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                  Import from Google Scholar
                </h4>
                <GoogleScholarFetcher 
                  v-model="formData.research_publications"
                  @publications-added="handleScholarPublications"
                />
              </div>

              <button
                type="button"
                @click="addPublicationEntry"
                class="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Another Publication
              </button>
            </fieldset>
          </div>
        </section> -->

        <!-- Student Supervision Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-yellow-600 to-orange-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
              Student Supervision
            </h2>
          </div>
          <div class="p-6">
            <ErrorDisplay 
              v-if="showValidationErrors && hasFieldError('supervisions')"
              :errors="getFieldError('supervisions')"
              class="mb-4"
            />
            
            <div class="space-y-6">
              <div v-for="(supervision, index) in formData.supervisions" :key="index" 
                   class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Student Supervision {{ index + 1 }}</h3>
                  <button
                    type="button"
                    @click="removeSupervisionEntry(index)"
                    class="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                
                <div class="grid grid-cols-1 gap-4">
                  <div class="form-group">
                    <label :for="`student_name_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Student Name *
                    </label>
                    <input
                      :id="`student_name_${index}`"
                      v-model="supervision.student_name"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`supervisions.${index}.student_name`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`supervisions.${index}.student_name`)"
                      placeholder="Enter student name"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`supervisions.${index}.student_name`)"
                      :errors="getFieldError(`supervisions.${index}.student_name`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`thesis_title_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Thesis Title *
                    </label>
                    <input
                      :id="`thesis_title_${index}`"
                      v-model="supervision.thesis_title"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`supervisions.${index}.thesis_title`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`supervisions.${index}.thesis_title`)"
                      placeholder="Enter thesis title"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`supervisions.${index}.thesis_title`)"
                      :errors="getFieldError(`supervisions.${index}.thesis_title`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`degree_level_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Degree Level *
                    </label>
                    <select
                      :id="`degree_level_${index}`"
                      v-model="supervision.degree_level"
                      :class="[
                        'form-control',
                        hasFieldError(`supervisions.${index}.degree_level`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @change="clearFieldError(`supervisions.${index}.degree_level`)"
                    >
                      <option value="">Select degree level</option>
                      <option v-for="option in supervisionFields.find(field => field.key === 'degree_level').options" 
                              :key="option" 
                              :value="option">
                        {{ option }}
                      </option>
                    </select>
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`supervisions.${index}.degree_level`)"
                      :errors="getFieldError(`supervisions.${index}.degree_level`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`supervision_year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      :id="`supervision_year_${index}`"
                      v-model.number="supervision.year"
                      type="number"
                      min="1950"
                      :max="new Date().getFullYear() + 10"
                      :class="[
                        'form-control',
                        hasFieldError(`supervisions.${index}.year`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`supervisions.${index}.year`)"
                      placeholder="Enter year"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`supervisions.${index}.year`)"
                      :errors="getFieldError(`supervisions.${index}.year`)"
                      class="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                @click="addSupervisionEntry"
                class="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors duration-200"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Student Supervision
              </button>
            </div>
          </div>
        </section>

        <!-- Academic Platforms Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-purple-600 to-indigo-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
              Academic Platforms <span class="text-red-500">&nbsp;*</span>
            </h2>
          </div>
          <div class="p-6">
            <ErrorDisplay 
              v-if="showValidationErrors && hasFieldError('professional_memberships')"
              :errors="getFieldError('professional_memberships')"
              class="mb-4"
            />
            
            <div class="space-y-6">
              <div v-for="(membership, index) in formData.professional_memberships" :key="index" 
                   class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Academic Platform {{ index + 1 }}</h3>
                  <button
                    type="button"
                    @click="removeProfessionalMembershipEntry(index)"
                    class="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="form-group">
                    <label :for="`google_scholar_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Google Scholar Link
                    </label>
                    <input
                      :id="`google_scholar_${index}`"
                      v-model="membership.google_scholar"
                      type="url"
                      placeholder="Enter Google Scholar profile link"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`research_gate_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Research Gate Link
                    </label>
                    <input
                      :id="`research_gate_${index}`"
                      v-model="membership.research_gate"
                      type="url"
                      placeholder="Enter Research Gate profile link"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`orcid_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      ORCID Link
                    </label>
                    <input
                      :id="`orcid_${index}`"
                      v-model="membership.orcid"
                      type="url"
                      placeholder="Enter ORCID profile link"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`linkedin_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      LinkedIn Link
                    </label>
                    <input
                      :id="`linkedin_${index}`"
                      v-model="membership.linkedin"
                      type="url"
                      placeholder="Enter LinkedIn profile link"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  
                  <!-- <div class="form-group md:col-span-2">
                    <label :for="`membership_place_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Academic Platform Place
                    </label>
                    <select
                      :id="`membership_place_${index}`"
                      v-model="membership.membership_place"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Select membership place</option>
                      <option value="Presenter">Presenter</option>
                      <option value="Reviewer">Reviewer</option>
                      <option value="Participant">Participant</option>
                      <option value="Attendance">Attendance</option>
                    </select>
                  </div> -->
                </div>
              </div>
              
              <button
                type="button"
                @click="addProfessionalMembershipEntry"
                class="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors duration-200"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Academic Platform
              </button>
            </div>
          </div>
        </section>

        <!-- Academic Conferences Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-teal-600 to-cyan-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
              Academic Conferences
            </h2>
          </div>
          <div class="p-6">
            <ErrorDisplay 
              v-if="showValidationErrors && hasFieldError('academic_conferences')"
              :errors="getFieldError('academic_conferences')"
              class="mb-4"
            />
            
            <div class="space-y-6">
              <div v-for="(conference, index) in formData.academic_conferences" :key="index" 
                   class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Academic Conference {{ index + 1 }}</h3>
                  <button
                    type="button"
                    @click="removeConferenceEntry(index)"
                    class="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                
                <div class="grid grid-cols-1 gap-4">
                  <div class="form-group">
                    <label :for="`conference_title_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Conference Title *
                    </label>
                    <input
                      :id="`conference_title_${index}`"
                      v-model="conference.conference_title"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_conferences.${index}.conference_title`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_conferences.${index}.conference_title`)"
                      placeholder="Enter conference title"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_conferences.${index}.conference_title`)"
                      :errors="getFieldError(`academic_conferences.${index}.conference_title`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`conference_location_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      :id="`conference_location_${index}`"
                      v-model="conference.location"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_conferences.${index}.location`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_conferences.${index}.location`)"
                      placeholder="Enter location"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_conferences.${index}.location`)"
                      :errors="getFieldError(`academic_conferences.${index}.location`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`conference_year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      :id="`conference_year_${index}`"
                      v-model.number="conference.year"
                      type="number"
                      min="1950"
                      :max="new Date().getFullYear() + 10"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_conferences.${index}.year`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_conferences.${index}.year`)"
                      placeholder="Enter year"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_conferences.${index}.year`)"
                      :errors="getFieldError(`academic_conferences.${index}.year`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`conference_role_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Role *
                    </label>
                    <select
                      :id="`conference_role_${index}`"
                      v-model="conference.role"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_conferences.${index}.role`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @change="clearFieldError(`academic_conferences.${index}.role`)"
                    >
                      <option value="">Select role</option>
                      <option value="Presenter">Presenter</option>
                      <option value="Attendee">Attendee</option>
                      <option value="Organizer">Organizer</option>
                      <option value="Reviewer">Reviewer</option>
                    </select>
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_conferences.${index}.role`)"
                      :errors="getFieldError(`academic_conferences.${index}.role`)"
                      class="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                @click="addConferenceEntry"
                class="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors duration-200"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Academic Conference
              </button>
            </div>
          </div>
        </section>

        <!-- Academic Workshops Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-pink-600 to-rose-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
              </svg>
              Academic Workshops
            </h2>
          </div>
          <div class="p-6">
            <ErrorDisplay 
              v-if="showValidationErrors && hasFieldError('academic_workshops')"
              :errors="getFieldError('academic_workshops')"
              class="mb-4"
            />
            
            <div class="space-y-6">
              <div v-for="(workshop, index) in formData.academic_workshops" :key="index" 
                   class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Academic Workshop {{ index + 1 }}</h3>
                  <button
                    type="button"
                    @click="removeWorkshopEntry(index)"
                    class="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                
                <div class="grid grid-cols-1 gap-4">
                  <div class="form-group">
                    <label :for="`workshop_title_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Workshop Title *
                    </label>
                    <input
                      :id="`workshop_title_${index}`"
                      v-model="workshop.workshop_title"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_workshops.${index}.workshop_title`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_workshops.${index}.workshop_title`)"
                      placeholder="Enter workshop title"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_workshops.${index}.workshop_title`)"
                      :errors="getFieldError(`academic_workshops.${index}.workshop_title`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`workshop_location_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      :id="`workshop_location_${index}`"
                      v-model="workshop.location"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_workshops.${index}.location`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_workshops.${index}.location`)"
                      placeholder="Enter location"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_workshops.${index}.location`)"
                      :errors="getFieldError(`academic_workshops.${index}.location`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`workshop_year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      :id="`workshop_year_${index}`"
                      v-model.number="workshop.year"
                      type="number"
                      min="1950"
                      :max="new Date().getFullYear() + 10"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_workshops.${index}.year`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_workshops.${index}.year`)"
                      placeholder="Enter year"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_workshops.${index}.year`)"
                      :errors="getFieldError(`academic_workshops.${index}.year`)"
                      class="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                @click="addWorkshopEntry"
                class="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors duration-200"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Academic Workshop
              </button>
            </div>
          </div>
        </section>

        <!-- Academic Trainings Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-gray-600 to-gray-800 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
              Academic Trainings
            </h2>
          </div>
          <div class="p-6">
            <ErrorDisplay 
              v-if="showValidationErrors && hasFieldError('academic_trainings')"
              :errors="getFieldError('academic_trainings')"
              class="mb-4"
            />
            
            <div class="space-y-6">
              <div v-for="(training, index) in formData.academic_trainings" :key="index" 
                   class="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-medium text-gray-900">Academic Training {{ index + 1 }}</h3>
                  <button
                    type="button"
                    @click="removeTrainingEntry(index)"
                    class="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                
                <div class="grid grid-cols-1 gap-4">
                  <div class="form-group">
                    <label :for="`training_title_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Training Title *
                    </label>
                    <input
                      :id="`training_title_${index}`"
                      v-model="training.training_title"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_trainings.${index}.training_title`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_trainings.${index}.training_title`)"
                      placeholder="Enter training title"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_trainings.${index}.training_title`)"
                      :errors="getFieldError(`academic_trainings.${index}.training_title`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`training_provider_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Provider *
                    </label>
                    <input
                      :id="`training_provider_${index}`"
                      v-model="training.provider"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_trainings.${index}.provider`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_trainings.${index}.provider`)"
                      placeholder="Enter provider"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_trainings.${index}.provider`)"
                      :errors="getFieldError(`academic_trainings.${index}.provider`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`training_year_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      :id="`training_year_${index}`"
                      v-model.number="training.year"
                      type="number"
                      min="1950"
                      :max="new Date().getFullYear() + 10"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_trainings.${index}.year`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_trainings.${index}.year`)"
                      placeholder="Enter year"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_trainings.${index}.year`)"
                      :errors="getFieldError(`academic_trainings.${index}.year`)"
                      class="mt-1"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label :for="`training_duration_${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                      Duration *
                    </label>
                    <input
                      :id="`training_duration_${index}`"
                      v-model="training.duration"
                      type="text"
                      :class="[
                        'form-control',
                        hasFieldError(`academic_trainings.${index}.duration`) ? 'border-red-300 focus:ring-red-500' : ''
                      ]"
                      @input="clearFieldError(`academic_trainings.${index}.duration`)"
                      placeholder="e.g., 3 days, 2 weeks"
                    />
                    <ErrorDisplay 
                      v-if="showValidationErrors && hasFieldError(`academic_trainings.${index}.duration`)"
                      :errors="getFieldError(`academic_trainings.${index}.duration`)"
                      class="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                @click="addTrainingEntry"
                class="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:border-gray-400 transition-colors duration-200"
              >
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Academic Training
              </button>
            </div>
          </div>
        </section>

        <!-- Biography Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="bg-gradient-to-r from-emerald-600 to-green-700 px-6 py-4">
            <h2 class="text-xl font-semibold text-white flex items-center">
              <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Biography <span class="text-red-500">&nbsp;*</span>
            </h2>
          </div>
          <div class="p-6">
            <fieldset class="space-y-6">
              <legend class="sr-only">Biography</legend>
              
              <div class="grid grid-cols-1 gap-6">
                <div class="form-group">
                  <label for="biography_en" class="block text-sm font-medium text-gray-700 mb-2">
                    Biography (English) <span class="text-red-500">*</span>
                  </label>
                  <textarea
                    id="biography_en"
                    v-model="formData.biography.biography_en"
                    rows="4"
                    required
                    :class="[
                      'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200',
                      hasFieldError('biography_en') ? 'border-red-500 focus:ring-red-500' : ''
                    ]"
                    placeholder="Write your professional biography in English..."
                    @input="clearFieldError('biography_en')"
                  ></textarea>
                  <ErrorDisplay 
                    v-if="hasFieldError('biography_en')"
                    :errors="form.errors"
                    field-name="biography_en"
                  />
                </div>
                <div class="form-group">
                  <label for="biography_ar" class="block text-sm font-medium text-gray-700 mb-2">
                    Biography (Arabic)
                  </label>
                  <textarea
                    id="biography_ar"
                    v-model="formData.biography.biography_ar"
                    rows="4"
                    dir="rtl"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Write your professional biography in Arabic..."
                  ></textarea>
                </div>
                <div class="form-group">
                  <label for="biography_ku" class="block text-sm font-medium text-gray-700 mb-2">
                    Biography (Kurdish)
                  </label>
                  <textarea
                    id="biography_ku"
                    v-model="formData.biography.biography_ku"
                    rows="4"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Write your professional biography in Kurdish..."
                  ></textarea>
                </div>
              </div>
            </fieldset>
          </div>
        </section>

        <!-- Form Actions -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div class="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div class="text-sm text-gray-600">
              <p>Only the core submission sections are required: Personal Information, Institution Information, Formal Photo, and Education.</p>
              <p class="mt-1">All other sections are optional, and your progress is automatically saved as you type.</p>
            </div>
            <div class="flex gap-4">
              <button
                type="button"
                @click="saveDraft"
                :disabled="form.saving"
                class="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <svg v-if="form.saving" class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ form.saving ? 'Saving...' : 'Save Draft' }}
              </button>
              <button
                type="submit"
                :disabled="!canSubmit"
                class="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <svg v-if="submitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ submitting ? 'Submitting Form...' : 'Submit Form' }}
              </button>
            </div>
          </div>
        </div>

      </form>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useForm } from '../stores/form';
import { useAuth } from '../stores/auth';
import SaveStatus from '../components/SaveStatus.vue';
import CollegeDepartmentSelect from '../components/CollegeDepartmentSelect.vue';
import FileUploader from '../components/FileUploader.vue';
import GoogleScholarFetcher from '../components/GoogleScholarFetcher.vue';
import ErrorDisplay from '../components/ErrorDisplay.vue';
import DatePicker from '../components/DatePicker.vue';
import type {
  ConferenceEntry,
  EducationEntry,
  ProfessionalMembershipEntry,
  PublicationEntry,
  ScientificTitleEntry,
  SubmitFilesSection,
  SubmitFormData,
  SubmitStoredFile,
  SupervisionEntry,
  TeachingSubjectEntry,
  TrainingEntry,
  WorkshopEntry,
} from '../types';

const router = useRouter();
const form = useForm();
const auth = useAuth();
const logoUrl = '/AUAS.png';
const submitting = ref(false);
const showValidationErrors = ref(false);

// Form data structure
const formData = ref<SubmitFormData>({
  personal_info: {
    name_en: '',
    name_ar: '',
    name_ku: '',
    title_en: '',
    title_ar: '',
    title_ku: '',
    gender: '',
    date_of_birth: '',
    nationality: 'Iraqi',
    email: '',
    phone: ''
  },
  institution_info: {
    college_institute: '',
    department: '',
    specialization_en: '',
    specialization_ar: '',
    specialization_ku: ''
  },
  files: {
    photo: null,
    cv_file: null
  },
  education: [],
  scientific_titles: [],
  teaching_subjects: [],
  research_publications: [],
  supervisions: [],
  professional_memberships: [],
  academic_conferences: [],
  academic_workshops: [],
  academic_trainings: [],
  biography: {
    biography_en: '',
    biography_ar: '',
    biography_ku: ''
  }
});

// Field definitions for multi-entry sections
const educationFields = [
  { key: 'degree', label: 'Degree', type: 'select', options: ['Bachelor', 'Master', 'PhD', 'Diploma'], required: true },
  { key: 'university', label: 'University/College', type: 'text', required: true },
  { key: 'year', label: 'Year', type: 'number', required: true },
  { key: 'country', label: 'Country', type: 'text', required: true }
];

const publicationFields = [
  { key: 'title', label: 'Title', type: 'text', required: true },
  { key: 'journal', label: 'Journal/Conference', type: 'text', required: true },
  { key: 'year', label: 'Year', type: 'number', required: true },
  { key: 'cited_by', label: 'Cited By', type: 'number' },
  { key: 'url', label: 'URL/DOI', type: 'url' }
];

const supervisionFields = [
  { key: 'student_name', label: 'Student Name', type: 'text', required: true },
  { key: 'thesis_title', label: 'Thesis Title', type: 'text', required: true },
  { key: 'degree_level', label: 'Degree Level', type: 'select', options: ['Diploma', 'Associate Degree', 'Bachelor', 'Bachelor Honors', 'Master', 'Master by Research', 'PhD', 'Postdoc', 'Certificate', 'Professional Degree', 'Others'], required: true },
  { key: 'year', label: 'Year', type: 'number', required: true }
];

const conferenceFields = [
  { key: 'conference_title', label: 'Conference Title', type: 'text', required: true },
  { key: 'location', label: 'Location', type: 'text', required: true },
  { key: 'year', label: 'Year', type: 'number', required: true },
  { key: 'role', label: 'Role', type: 'select', options: ['Presenter', 'Attendee', 'Organizer', 'Reviewer'], required: true }
];

const workshopFields = [
  { key: 'workshop_title', label: 'Workshop Title', type: 'text', required: true },
  { key: 'location', label: 'Location', type: 'text', required: true },
  { key: 'year', label: 'Year', type: 'number', required: true }
];

const trainingFields = [
  { key: 'training_title', label: 'Training Title', type: 'text', required: true },
  { key: 'provider', label: 'Provider', type: 'text', required: true },
  { key: 'year', label: 'Year', type: 'number', required: true },
  { key: 'duration', label: 'Duration', type: 'text', required: true }
];

const hasTextValue = (value: unknown) => typeof value === 'string' && value.trim() !== '';

const resolveSessionEmail = () => {
  return auth.user?.email?.trim().toLowerCase() || auth.email?.trim().toLowerCase() || '';
};

const syncSessionEmail = () => {
  const sessionEmail = resolveSessionEmail();
  if (sessionEmail) {
    formData.value.personal_info.email = sessionEmail;
  }
};

const hasMeaningfulEntries = (entries: Array<Record<string, unknown>>) => {
  return entries.some((entry) =>
    Object.entries(entry).some(([key, value]) => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      if (typeof value === 'number') {
        return key !== 'year' && key !== 'start_year' && key !== 'end_year' && value !== 0;
      }

      return value === true;
    }),
  );
};

const normalizeEducationEntriesForUi = () => {
  formData.value.education = (formData.value.education || []).map((entry) => {
    const normalized = form.normalizeEducationEntry(entry as EducationEntry & Record<string, unknown>);
    const fallbackYear = normalized.end_year || normalized.start_year || normalized.year || normalized.graduation_year || '';

    return {
      ...normalized,
      year: normalized.year || fallbackYear || '',
      start_year: normalized.start_year || '',
      end_year: normalized.end_year || (!normalized.start_year ? fallbackYear : ''),
    };
  });
};

const buildValidationPayload = (): SubmitFormData => {
  const sessionEmail = resolveSessionEmail();
  const normalizedEducation = (formData.value.education || [])
    .map((entry) => form.normalizeEducationEntry(entry as EducationEntry & Record<string, unknown>))
    .filter((entry) => !form.isEmptyEducationEntry(entry));

  return {
    ...formData.value,
    personal_info: {
      ...formData.value.personal_info,
      email: sessionEmail || formData.value.personal_info.email,
    },
    institution_info: {
      ...formData.value.institution_info,
    },
    files: {
      ...formData.value.files,
    },
    education: normalizedEducation,
    scientific_titles: formData.value.scientific_titles.map((entry) => ({ ...entry })),
    teaching_subjects: formData.value.teaching_subjects.map((entry) => ({ ...entry })),
    research_publications: formData.value.research_publications.map((entry) => ({ ...entry })),
    supervisions: formData.value.supervisions.map((entry) => ({ ...entry })),
    professional_memberships: formData.value.professional_memberships.map((entry) => ({ ...entry })),
    academic_conferences: formData.value.academic_conferences.map((entry) => ({ ...entry })),
    academic_workshops: formData.value.academic_workshops.map((entry) => ({ ...entry })),
    academic_trainings: formData.value.academic_trainings.map((entry) => ({ ...entry })),
    biography: {
      ...formData.value.biography,
    },
  };
};

// Computed properties
const sectionCompletionStates = computed(() => [
  isPersonalInfoComplete.value,
  isInstitutionInfoComplete.value,
  isFilesComplete.value,
  hasMeaningfulEntries(formData.value.education as Array<Record<string, unknown>>),
  hasMeaningfulEntries(formData.value.scientific_titles as Array<Record<string, unknown>>),
  hasMeaningfulEntries(formData.value.teaching_subjects as Array<Record<string, unknown>>),
  hasMeaningfulEntries(formData.value.supervisions as Array<Record<string, unknown>>),
  hasMeaningfulEntries(formData.value.professional_memberships as Array<Record<string, unknown>>),
  hasMeaningfulEntries(formData.value.academic_conferences as Array<Record<string, unknown>>),
  hasMeaningfulEntries(formData.value.academic_workshops as Array<Record<string, unknown>>),
  hasMeaningfulEntries(formData.value.academic_trainings as Array<Record<string, unknown>>),
  isBiographyComplete.value,
]);

const totalSections = computed(() => sectionCompletionStates.value.length);
const completedSections = computed(() => {
  return sectionCompletionStates.value.filter(Boolean).length;
});

const progressPercentage = computed(() => {
  return Math.round((completedSections.value / totalSections.value) * 100);
});

const isPersonalInfoComplete = computed(() => {
  const info = buildValidationPayload().personal_info;
  return !!(info.name_en && info.title_en && info.gender && info.date_of_birth &&
    info.nationality && info.email && info.phone);
});

const isInstitutionInfoComplete = computed(() => {
  const info = formData.value.institution_info;
  return !!(info.college_institute && info.department && info.specialization_en);
});

const isFilesComplete = computed(() => {
  return hasTextValue(formData.value.files.photo);
});

const isBiographyComplete = computed(() => {
  const bio = formData.value.biography;
  return !!(hasTextValue(bio.biography_en) || hasTextValue(bio.biography_ar) || hasTextValue(bio.biography_ku));
});

// Methods
const addEducationEntry = () => {
  formData.value.education.push({
    degree: '',
    university: '',
    year: new Date().getFullYear(),
    start_year: '',
    end_year: '',
    country: ''
  });
};

const removeEducationEntry = (index: number) => {
  formData.value.education.splice(index, 1);
};

const addScientificTitleEntry = () => {
  formData.value.scientific_titles.push({
    title: '',
    document_number: '',
    year: new Date().getFullYear()
  });
};

const removeScientificTitleEntry = (index: number) => {
  formData.value.scientific_titles.splice(index, 1);
};

const addTeachingSubjectEntry = () => {
  formData.value.teaching_subjects.push({
    subject: '',
    college_institute: '',
    semester: 0,
    year: new Date().getFullYear()
  });
};

const removeTeachingSubjectEntry = (index: number) => {
  formData.value.teaching_subjects.splice(index, 1);
};

const addPublicationEntry = () => {
  formData.value.research_publications.push({
    title: '',
    journal: '',
    year: new Date().getFullYear(),
    cited_by: 0,
    url: ''
  });
};

const removePublicationEntry = (index: number) => {
  formData.value.research_publications.splice(index, 1);
};

const addSupervisionEntry = () => {
  formData.value.supervisions.push({
    student_name: '',
    thesis_title: '',
    degree_level: '',
    year: new Date().getFullYear()
  });
};

const removeSupervisionEntry = (index: number) => {
  formData.value.supervisions.splice(index, 1);
};

const addProfessionalMembershipEntry = () => {
  formData.value.professional_memberships.push({
    google_scholar: '',
    research_gate: '',
    orcid: '',
    linkedin: '',
    membership_place: ''
  });
};

const removeProfessionalMembershipEntry = (index: number) => {
  formData.value.professional_memberships.splice(index, 1);
};

const addConferenceEntry = () => {
  formData.value.academic_conferences.push({
    conference_title: '',
    location: '',
    year: new Date().getFullYear(),
    role: ''
  });
};

const removeConferenceEntry = (index: number) => {
  formData.value.academic_conferences.splice(index, 1);
};

const addWorkshopEntry = () => {
  formData.value.academic_workshops.push({
    workshop_title: '',
    location: '',
    year: new Date().getFullYear()
  });
};

const removeWorkshopEntry = (index: number) => {
  formData.value.academic_workshops.splice(index, 1);
};

const addTrainingEntry = () => {
  formData.value.academic_trainings.push({
    training_title: '',
    provider: '',
    year: new Date().getFullYear(),
    duration: ''
  });
};

const removeTrainingEntry = (index: number) => {
  formData.value.academic_trainings.splice(index, 1);
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 5; year >= 1950; year--) {
    years.push(year);
  }
  return years;
};

/**
 * Handle file upload event from FileUploader.
 * Normalize and persist only the file URL string in formData.files
 * to align with server schema and ensure consistent v-model binding.
 */
const handleFileUpload = (fieldName: keyof SubmitFilesSection, fileData: SubmitStoredFile) => {
  // Clear any file-related errors when a file is uploaded
  form.removeError('cv_file');
  form.removeError('photo');

  const toUrl = (val: SubmitStoredFile | string | null) => {
    if (typeof val === 'string') return val;
    return val?.url || val?.path || '';
  };

  formData.value.files[fieldName] = toUrl(fileData) || null;
  
  // Auto-save the form after file upload
  saveDraft();
};

const loadExistingFiles = async () => {
  // Files are loaded through /api/form/current during bootstrap.
  return Promise.resolve();
};

const handleScholarPublications = (publications: any[]) => {
  publications.forEach(pub => {
    // Create a publication object matching the defined fields
    const publicationEntry = {
      title: pub.title || '',
      journal: pub.venue || pub.journal || '',
      year: pub.year ? parseInt(pub.year.toString()) : new Date().getFullYear(),
      cited_by: pub.citedBy ? parseInt(pub.citedBy.toString()) : 0,
      url: pub.url || ''
    };
    
    // Add to research publications array
    formData.value.research_publications.push(publicationEntry);
  });
  
  // Auto-save the form after adding publications
  saveDraft();
};

const saveDraft = async () => {
  try {
    syncSessionEmail();
    await form.saveAllSections(formData.value);
    // Clear validation errors on successful save
    showValidationErrors.value = false;
  } catch (error: any) {
    // console.error('Error saving draft:', error);
    // Show user-friendly error message
    form.addError('save', error.message || 'Failed to save draft');
  }
};

const submitForm = async () => {
  if (!form.isSubmittable) {
    return;
  }
  
  submitting.value = true;
  showValidationErrors.value = false;
  form.clearErrors();
  
  try {
    syncSessionEmail();
    const payload = buildValidationPayload();

    // Validate form data before submission
    const validation = form.validateFormData(payload);
    if (!validation.isValid) {
      form.errors = validation.errors;
      showValidationErrors.value = true;
      await scrollToFirstError();
      return;
    }

    Object.assign(formData.value, payload);
    await form.saveAllSections(payload);
    await form.submit();
    
    // Mark submission lock locally to hard-block client-side navigation
    try { localStorage.setItem('auas_submitted', 'true'); } catch (_) {}

    // Log out and redirect to login after successful submission
    await auth.logout();
    // Replace history to prevent going back into dashboard or form
    router.replace('/login?submitted=true');
  } catch (error: any) {
    // console.error('Error submitting form:', error);
    showValidationErrors.value = true;
    await scrollToFirstError();
  } finally {
    submitting.value = false;
  }
};

// Validation helpers
const getFieldError = (fieldName: string) => {
  return form.errors
    .filter((error) => error.field === fieldName || error.field.startsWith(`${fieldName}.`))
    .map((error) => error.message);
};

const hasFieldError = (fieldName: string) => {
  return getFieldError(fieldName).length > 0;
};

// Clear errors when user starts typing
const clearFieldError = (fieldName: string) => {
  form.errors = form.errors.filter((error) => error.field !== fieldName && !error.field.startsWith(`${fieldName}.`));
};

const getElementIdForError = (fieldName: string) => {
  if (fieldName.startsWith('education.')) {
    const [, index, key] = fieldName.split('.');

    if (key === 'year' || key === 'start_year' || key === 'end_year') {
      return `start_year_${index}`;
    }

    return `${key}_${index}`;
  }

  return fieldName;
};

const scrollToFirstError = async () => {
  await nextTick();

  const firstError = form.errors[0];
  if (!firstError) {
    return;
  }

  const targetId = getElementIdForError(firstError.field);
  const target = document.getElementById(targetId);

  if (!target) {
    return;
  }

  target.scrollIntoView({ behavior: 'smooth', block: 'center' });

  if ('focus' in target && typeof target.focus === 'function') {
    target.focus({ preventScroll: true });
  }
};

// Auto-save functionality with throttling to prevent 429 errors
let saveTimeout: any;
let lastSaveTime = 0;
let isBootstrapping = false;
const MIN_SAVE_INTERVAL = 5000; // Minimum 5 seconds between saves

const autoSave = () => {
  if (isBootstrapping) return;
  clearTimeout(saveTimeout);
  
  const now = Date.now();
  const timeSinceLastSave = now - lastSaveTime;
  
  if (timeSinceLastSave < MIN_SAVE_INTERVAL) {
    // If we saved recently, delay the next save
    const delay = MIN_SAVE_INTERVAL - timeSinceLastSave + 1000; // Add 1 second buffer
    saveTimeout = setTimeout(() => {
      saveDraft();
      lastSaveTime = Date.now();
    }, delay);
  } else {
    // Safe to save immediately
    saveTimeout = setTimeout(() => {
      saveDraft();
      lastSaveTime = Date.now();
    }, 2000);
  }
};

// Watch for changes and auto-save
watch(formData, autoSave, { deep: true });

// Computed properties
const canSubmit = computed(() => {
  return form.isSubmittable && !submitting.value;
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 1950; year <= currentYear + 10; year++) {
    years.push(year);
  }
  return years;
});

// Initialize form data
onMounted(async () => {
  try {
    isBootstrapping = true;
    await form.bootstrap();
    if (form.sections) {
      Object.assign(formData.value, form.sections);
      normalizeEducationEntriesForUi();
      syncSessionEmail();
      
      // Normalize gender to lowercase to match select options
      if (formData.value.personal_info?.gender) {
        formData.value.personal_info.gender = formData.value.personal_info.gender.toLowerCase();
      }
      
      // Normalize date_of_birth from ISO string to yyyy-MM-dd format for HTML date input
      if (formData.value.personal_info?.date_of_birth) {
        const dateValue = formData.value.personal_info.date_of_birth;
        if (typeof dateValue === 'string' && dateValue.includes('T')) {
          // Convert ISO string to yyyy-MM-dd format
          formData.value.personal_info.date_of_birth = dateValue.split('T')[0];
        }
      }
    }
    
    // Load existing files if they exist
    await loadExistingFiles();
    
    // Initialize with one entry for each new section if empty
    if (formData.value.scientific_titles.length === 0) {
      addScientificTitleEntry();
    }
    if (formData.value.teaching_subjects.length === 0) {
      addTeachingSubjectEntry();
    }
  } catch (error) {
    // console.error('Error loading form data:', error);
  } finally {
    // Delay turning off bootstrap flag to allow Vue reactivity to settle
    setTimeout(() => { isBootstrapping = false; }, 100);
  }
});
</script>

<style scoped>
@reference "../styles.css";

.form-group {
  @apply space-y-1;
}

.form-control {
  @apply w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200;
}

.form-control:focus {
  @apply outline-none ring-2 ring-blue-500 border-transparent;
}

.btn {
  @apply inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200;
}

.btn-primary {
  @apply text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500;
}

.btn-secondary {
  @apply text-gray-700 bg-white border-gray-300 hover:bg-gray-50 focus:ring-indigo-500;
}

.alert {
  @apply p-4 rounded-md;
}

.alert-danger {
  @apply bg-red-50 border border-red-200 text-red-700;
}

.alert-success {
  @apply bg-green-50 border border-green-200 text-green-700;
}
</style>
